import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Container } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  FaBuilding,
  FaChartBar,
  FaClock,
  FaCloud,
  FaDesktop,
  FaExclamationTriangle,
  FaHdd,
  FaInfoCircle,
  FaLayerGroup,
  FaLink,
  FaServer,
  FaSpinner,
  FaSyncAlt,
  FaWindows,
} from "react-icons/fa";

import { fetchAssetsOverview, fetchAssetsSyncStatus } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";
import { useAuth } from "../../contexts/AuthContext";
import AssetMetricCard from "../../components/AssetMetricCard";
import AssetsTable from "../../components/tables/AssetsTable";
import AssetMappingPanel from "../../components/tables/AssetMappingPanel";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ACCENT = "#6366f1";

/** Green gradient for hardware vendor bars — darkest for top vendor, lighter for smaller counts. */
const MANUFACTURER_BAR_COLORS = [
  "#059669",
  "#10b981",
  "#34d399",
  "#6ee7b7",
  "#86efac",
  "#bbf7d0",
];
const MANUFACTURER_BAR_HOVER = [
  "#047857",
  "#059669",
  "#10b981",
  "#34d399",
  "#6ee7b7",
  "#86efac",
];

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.12 } },
};

const CHART_MIN_HEIGHT = 300;

const isDarkMode = () => document.body.classList.contains("dark-mode");

const gridColor = () => (isDarkMode() ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)");
const tickColor = () => (isDarkMode() ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.6)");

const relativeTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/** Card shell shared by every chart panel on this page. */
const ChartPanel = ({ icon: Icon, title, subtitle, children, footer }) => (
  <Card
    style={{
      borderRadius: 16,
      background: "var(--color-bg-card)",
      border: "1px solid var(--color-border)",
      boxShadow: "var(--shadow-sm)",
      height: "100%",
    }}
  >
    <Card.Body className="p-3 d-flex flex-column">
      <div className="d-flex align-items-center gap-2 mb-3">
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: "rgba(99,102,241,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={15} color={ACCENT} />
        </div>
        <div>
          <h6 className="fw-bold m-0" style={{ color: "var(--color-text)" }}>{title}</h6>
          {subtitle && <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>{subtitle}</span>}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: CHART_MIN_HEIGHT, position: "relative" }}>{children}</div>

      {footer && <div className="mt-2">{footer}</div>}
    </Card.Body>
  </Card>
);

const EmptyChart = ({ message }) => (
  <div className="d-flex align-items-center justify-content-center h-100" style={{ minHeight: CHART_MIN_HEIGHT }}>
    <span style={{ color: "var(--color-text-light)", fontSize: 13 }}>{message}</span>
  </div>
);

/**
 * Full-page state card for the "no client", "loading" and "no data" cases.
 *
 * `compact` shrinks the card when something actionable is rendered underneath it, so the
 * next step stays on screen instead of being pushed below the fold.
 */
const StateCard = ({ icon: Icon, spin, title, message, hint, compact = false, tint = "var(--color-primary)" }) => (
  <Container fluid className={compact ? "pt-3 pb-2" : "py-4"}>
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        style={{
          borderRadius: 20,
          background: "var(--color-bg-card)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-md)",
          overflow: "hidden",
        }}
      >
        <Card.Body className={compact ? "p-4" : "p-5"}>
          <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: compact ? "30vh" : "60vh" }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              style={{
                width: compact ? 84 : 120,
                height: compact ? 84 : 120,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(99,102,241,0.16) 0%, rgba(139,92,246,0.08) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: compact ? 16 : 24,
                border: "3px solid rgba(99,102,241,0.2)",
              }}
            >
              {spin ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                  <Icon size={compact ? 34 : 48} color={tint} />
                </motion.div>
              ) : (
                <Icon size={compact ? 34 : 48} color={tint} />
              )}
            </motion.div>

            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="fw-bold mb-3"
              style={{ fontSize: compact ? 22 : 28, color: "var(--color-text)", marginBottom: 16 }}
            >
              {title}
            </motion.h3>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-0"
              style={{ fontSize: compact ? 14 : 16, color: "var(--color-text-light)", maxWidth: 560, lineHeight: 1.6 }}
            >
              {message}
            </motion.p>

            {hint && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="d-flex align-items-center gap-2 mt-3"
                style={{
                  background: "rgba(99,102,241,0.08)",
                  border: "1px solid rgba(99,102,241,0.2)",
                  borderRadius: 12,
                  padding: "10px 14px",
                  maxWidth: 620,
                  textAlign: "left",
                }}
              >
                <FaInfoCircle size={13} color={ACCENT} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: "var(--color-text-light)", lineHeight: 1.5 }}>{hint}</span>
              </motion.div>
            )}
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  </Container>
);

/**
 * Assets dashboard: the hardware inventory OneView keeps in sync with ConnectWise RMM.
 * Aggregates come from the API so the browser never has to hold a full device list.
 */
const AssetsOverview = ({ clientId, selectedClient }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const { isStaffUser } = useAuth();
  const isStaff = isStaffUser();

  const [data, setData] = useState(null);
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!clientId) {
      setData(null);
      setLoading(false);
      return;
    }

    const cacheKey = `assets_overview_${clientId}`;
    const cached = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      setData(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchAssetsOverview(clientId)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        if (result) setCachedData(cacheKey, result, clientId);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, getCachedData, setCachedData]);

  useEffect(() => {
    let cancelled = false;
    fetchAssetsSyncStatus()
      .then((result) => {
        if (!cancelled) setSync(result);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const summary = data?.summary;
  const staleDays = data?.stale_threshold_days || 30;
  const clientName = selectedClient?.client_name || "";

  const osChart = useMemo(() => {
    const rows = data?.operating_systems || [];
    if (rows.length === 0) return null;

    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: "Devices",
          data: rows.map((row) => row.count),
          backgroundColor: "rgba(99,102,241,0.75)",
          hoverBackgroundColor: ACCENT,
          borderRadius: 6,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    };
  }, [data]);

  const manufacturerChart = useMemo(() => {
    const rows = data?.manufacturers || [];
    if (rows.length === 0) return null;

    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: "Devices",
          data: rows.map((row) => row.count),
          backgroundColor: rows.map((_, index) => MANUFACTURER_BAR_COLORS[index % MANUFACTURER_BAR_COLORS.length]),
          hoverBackgroundColor: rows.map((_, index) => MANUFACTURER_BAR_HOVER[index % MANUFACTURER_BAR_HOVER.length]),
          borderRadius: 999,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    };
  }, [data]);

  const recencyChart = useMemo(() => {
    const rows = data?.recency || [];
    if (rows.length === 0 || rows.every((row) => row.count === 0)) return null;

    return {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: "Devices",
          data: rows.map((row) => row.count),
          backgroundColor: rows.map((row) => row.color),
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 54,
        },
      ],
    };
  }, [data]);

  const horizontalBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      plugins: { legend: { display: false } },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: gridColor() },
          ticks: { color: tickColor(), precision: 0, font: { size: 11 } },
        },
        y: {
          grid: { display: false },
          ticks: {
            color: tickColor(),
            font: { size: 11 },
            callback(value) {
              const label = this.getLabelForValue(value);
              return label.length > 34 ? `${label.slice(0, 33)}...` : label;
            },
          },
        },
      },
    }),
    []
  );

  const verticalBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false }, ticks: { color: tickColor(), font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: gridColor() }, ticks: { color: tickColor(), precision: 0, font: { size: 11 } } },
      },
    }),
    []
  );

  if (!clientId) {
    return (
      <StateCard
        icon={FaHdd}
        title="Select a Client"
        message="Please select a client from the search bar above to view their managed asset inventory."
      />
    );
  }

  if (loading) {
    return (
      <StateCard
        icon={FaSpinner}
        spin
        title="Preparing Asset Inventory"
        message="Loading ConnectWise managed devices for the selected client..."
      />
    );
  }

  if (!data || !summary || summary.total_assets === 0) {
    const feed = data?.feed;
    const who = clientName || "this client";
    const needsMapping = feed?.mapped_companies === 0 && (feed?.unmapped_with_devices || 0) > 0;

    let emptyState;
    if (!feed?.has_any_data) {
      emptyState = {
        icon: FaSyncAlt,
        title: "ConnectWise Sync Has Not Run Yet",
        message: "OneView has not yet pulled any devices from ConnectWise RMM, so every client's Assets page is empty.",
        hint: "Add the ConnectWise API credentials to the environment, then run the sync (manage.py sync_connectwise). Once the first run finishes, assets appear here automatically.",
        showMapping: false,
      };
    } else if (feed.mapped_companies > 0) {
      emptyState = {
        icon: FaExclamationTriangle,
        title: "No Devices Reported",
        message: `${who} is linked to ConnectWise, but its company currently reports no managed devices.`,
        hint: "This usually means the RMM agent has not been deployed to any endpoint for this client, or every endpoint was removed from ConnectWise.",
        showMapping: false,
      };
    } else if (needsMapping) {
      emptyState = {
        icon: FaLink,
        title: "Client Not Linked to ConnectWise",
        message: `A ConnectWise company with devices is available, but it is not linked to ${who} yet.`,
        hint: isStaff
          ? "Use the mapping panel below to link the matching ConnectWise company. Its devices will appear here immediately."
          : "A Versetal administrator needs to link this client to its ConnectWise company.",
        showMapping: isStaff,
      };
    } else {
      emptyState = {
        icon: FaHdd,
        title: "Not Managed in ConnectWise",
        message: `OneView does not have ConnectWise RMM inventory for ${who}.`,
        hint: isStaff
          ? "This usually means the client is not onboarded to ConnectWise, or their company is not in this ConnectWise tenant. No mapping is needed on this page — confirm with the RMM team if assets should appear here."
          : "Asset inventory will appear here once this client is managed in ConnectWise RMM. Contact your Versetal team if you expected to see devices.",
        showMapping: false,
      };
    }

    return (
      <>
        <StateCard
          icon={emptyState.icon}
          tint="var(--color-text-light)"
          title={emptyState.title}
          message={emptyState.message}
          hint={emptyState.hint}
          compact={emptyState.showMapping}
        />
        {emptyState.showMapping && (
          <div className="px-3 pb-4">
            <AssetMappingPanel defaultOpen />
          </div>
        )}
      </>
    );
  }

  const syncLabel = relativeTime(sync?.finished_at || sync?.started_at);
  const stalePercent = summary.total_assets ? Math.round((summary.stale_30d / summary.total_assets) * 100) : 0;

  return (
    <motion.div className="p-3" initial="initial" animate="animate" variants={staggerContainer}>
      {/* Banner */}
      <motion.div variants={fadeUp} className="mb-3" style={{ marginTop: isMobile ? "0.5rem" : "-1.5rem" }}>
        <Card
          style={{
            borderRadius: 18,
            background: isDarkMode()
              ? "linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(14,15,26,0.4) 55%, rgba(6,182,212,0.12) 100%)"
              : "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(255,255,255,0.95) 52%, rgba(6,182,212,0.08) 100%)",
            border: "1px solid var(--color-border)",
            boxShadow: "0 10px 28px color-mix(in srgb, var(--color-text) 5%, transparent)",
            overflow: "hidden",
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center justify-content-between gap-3" style={{ flexWrap: "wrap" }}>
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 46,
                    height: 46,
                    background: "color-mix(in srgb, #6366f1 18%, var(--color-bg-card))",
                    border: "1px solid color-mix(in srgb, #6366f1 24%, transparent)",
                    borderRadius: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "inset 0 1px 0 color-mix(in srgb, #fff 40%, transparent)",
                  }}
                >
                  <FaHdd size={18} color={ACCENT} />
                </div>

                <div>
                  <h4 className="fw-bold mb-0" style={{ fontSize: 18, color: "var(--color-text)", letterSpacing: "-0.01em" }}>
                    Assets Dashboard For{clientName && <span style={{ color: ACCENT, marginLeft: 8 }}>{clientName}</span>}
                  </h4>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    Managed device inventory from ConnectWise RMM
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                <div
                  style={{
                    background: "color-mix(in srgb, #6366f1 12%, var(--color-bg-card))",
                    border: "1px solid color-mix(in srgb, #6366f1 20%, transparent)",
                    borderRadius: 999,
                    padding: "7px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaChartBar size={13} color={ACCENT} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: ACCENT }}>
                    {summary.total_assets.toLocaleString()} Assets
                  </span>
                </div>

                <div
                  title={sync?.status ? `Last ConnectWise sync: ${sync.status}` : "Waiting for the first ConnectWise sync"}
                  style={{
                    background: "color-mix(in srgb, #10b981 12%, var(--color-bg-card))",
                    border: "1px solid color-mix(in srgb, #10b981 20%, transparent)",
                    borderRadius: 999,
                    padding: "7px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaSyncAlt size={12} color="#10b981" />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#059669" }}>
                    {syncLabel ? `Synced ${syncLabel}` : "Awaiting first sync"}
                  </span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* Summary metrics — featured left + 2x3 grid (matches reference UI) */}
      <Row className="g-3 mb-4 align-items-stretch">
        <Col lg={3} md={12} className="d-flex">
          <motion.div variants={fadeUp} className="w-100 h-100" style={{ minHeight: isMobile ? 168 : undefined }}>
            <div style={{ height: "100%" }}>
              <AssetMetricCard
                featured
                title="Total Managed Assets"
                value={summary.total_assets.toLocaleString()}
                icon={FaLayerGroup}
                color="#8B5CF6"
                hint="Live inventory from ConnectWise RMM"
              />
            </div>
          </motion.div>
        </Col>

        <Col lg={9} md={12}>
          <Row className="g-3 h-100">
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title="Servers"
                  value={summary.servers.toLocaleString()}
                  icon={FaServer}
                  color="#3B82F6"
                  hint={summary.total_assets ? `${Math.round((summary.servers / summary.total_assets) * 100)}% of estate` : undefined}
                  progress={summary.total_assets ? Math.round((summary.servers / summary.total_assets) * 100) : 0}
                />
              </motion.div>
            </Col>
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title="Workstations"
                  value={summary.workstations.toLocaleString()}
                  icon={FaDesktop}
                  color="#3B82F6"
                  hint={summary.total_assets ? `${Math.round((summary.workstations / summary.total_assets) * 100)}% of estate` : undefined}
                  progress={summary.total_assets ? Math.round((summary.workstations / summary.total_assets) * 100) : 0}
                />
              </motion.div>
            </Col>
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title={`Quiet ${staleDays}+ Days`}
                  value={summary.stale_30d.toLocaleString()}
                  icon={FaClock}
                  color="#F59E0B"
                  hint={summary.stale_30d > 0 ? "May need attention" : "All checking in"}
                  progress={summary.total_assets ? Math.round((summary.stale_30d / summary.total_assets) * 100) : 0}
                />
              </motion.div>
            </Col>
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title="Virtual Machines"
                  value={summary.virtual_assets.toLocaleString()}
                  icon={FaCloud}
                  color="#10B981"
                  hint="Hyper-V / VMware guests"
                  progress={summary.total_assets ? Math.round((summary.virtual_assets / summary.total_assets) * 100) : 0}
                />
              </motion.div>
            </Col>
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title="Active This Week"
                  value={summary.active_7d.toLocaleString()}
                  icon={FaSyncAlt}
                  color="#10B981"
                  hint="Checked in last 7 days"
                  progress={summary.total_assets ? Math.round((summary.active_7d / summary.total_assets) * 100) : 0}
                />
              </motion.div>
            </Col>
            <Col md={4} sm={6}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <AssetMetricCard
                  title="Operating Systems"
                  value={summary.distinct_os.toLocaleString()}
                  icon={FaWindows}
                  color="#F59E0B"
                  hint={`${summary.distinct_os} Distinct platforms`}
                  progress={Math.min(100, Math.round((summary.distinct_os / 30) * 100))}
                />
              </motion.div>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Operating Systems + Hardware Vendors */}
      <Row className="mb-4 align-items-stretch">
        <Col lg={7} className={`${isMobile ? "mb-3" : ""} d-flex`}>
          <motion.div variants={fadeUp} className="w-100" style={{ height: "100%" }}>
            <ChartPanel icon={FaWindows} title="Operating Systems" subtitle="Top platforms across the estate">
              {osChart ? <Bar data={osChart} options={horizontalBarOptions} /> : <EmptyChart message="No operating systems reported" />}
            </ChartPanel>
          </motion.div>
        </Col>

        <Col lg={5} className="d-flex">
          <motion.div variants={fadeUp} className="w-100" style={{ height: "100%" }}>
            <ChartPanel icon={FaBuilding} title="Hardware Vendors" subtitle="Who supplies this client's fleet">
              {manufacturerChart ? (
                <Bar data={manufacturerChart} options={horizontalBarOptions} />
              ) : (
                <EmptyChart message="No manufacturers reported" />
              )}
            </ChartPanel>
          </motion.div>
        </Col>
      </Row>

      {/* Inventory */}
      <motion.div variants={fadeUp} className="mb-4">
        <AssetsTable key={clientId} clientId={clientId} clientName={clientName} staleDays={staleDays} />
      </motion.div>

      {/* When Devices Were Last Seen */}
      <Row className="mb-4">
        <Col>
          <motion.div variants={fadeUp}>
            <ChartPanel
              icon={FaClock}
              title="When Devices Were Last Seen"
              subtitle="How many devices checked in recently vs. ones that have gone quiet"
              footer={
                <div
                  className="d-flex align-items-center gap-2"
                  style={{
                    background: summary.stale_30d > 0 ? "rgba(245,158,11,0.08)" : "rgba(16,185,129,0.08)",
                    border: `1px solid ${summary.stale_30d > 0 ? "rgba(245,158,11,0.25)" : "rgba(16,185,129,0.25)"}`,
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}
                >
                  {summary.stale_30d > 0 ? (
                    <FaExclamationTriangle size={12} color="#f59e0b" />
                  ) : (
                    <FaSyncAlt size={12} color="#10b981" />
                  )}
                  <span style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    {summary.stale_30d > 0
                      ? `${summary.stale_30d.toLocaleString()} devices (${stalePercent}%) have not checked in for ${staleDays}+ days — these are candidates for decommissioning or agent repair.`
                      : `Every managed device has checked in within the last ${staleDays} days.`}
                  </span>
                </div>
              }
            >
              {recencyChart ? <Bar data={recencyChart} options={verticalBarOptions} /> : <EmptyChart message="No check-in data available" />}
            </ChartPanel>
          </motion.div>
        </Col>
      </Row>

      {isStaff && (
        <motion.div variants={fadeUp}>
          <AssetMappingPanel />
        </motion.div>
      )}
    </motion.div>
  );
};

export default AssetsOverview;
